import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Chip,
    Stack,
    IconButton,
    useTheme,
    alpha,
    Divider,
    Autocomplete
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../../components/Iconify';
import { getInstrumentList } from '../../../redux/slices/trades';
import modifyScriptName from '../../../utils/modifyScriptName';

// Styled components for compact modal design
const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: theme.shape.borderRadius * 2,
        maxWidth: '90vw',
        width: '100%',
        maxHeight: '90vh',
        margin: theme.spacing(1),
        [theme.breakpoints.up('sm')]: {
            maxWidth: 700,
        },
        overflow: 'visible',
        boxShadow: theme.shadows[24],
    },
}));

const CompactCard = styled(Card)(({ theme, side }) => ({
    backgroundColor: side === 'BUY'
        ? alpha(theme.palette.success.main, 0.05)
        : alpha(theme.palette.error.main, 0.05),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${side === 'BUY'
        ? alpha(theme.palette.success.main, 0.2)
        : alpha(theme.palette.error.main, 0.2)
        }`,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: theme.shadows[2],
}));

const CompactCardHeader = styled(Box)(({ theme, side }) => ({
    padding: theme.spacing(1.5, 2),
    backgroundColor: side === 'BUY'
        ? alpha(theme.palette.success.main, 0.1)
        : alpha(theme.palette.error.main, 0.1),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minHeight: 48,
}));

const ScriptItem = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    cursor: 'pointer',
    transition: theme.transitions.create(['background-color'], {
        duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
}));

const HedgingWatchlistModal = ({ open, onClose }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    // Redux state
    const { instrumentData, isLoading } = useSelector((state) => state?.trade);

    // Local state
    const [formData, setFormData] = useState({
        script1: {
            side: 'BUY',
            scriptId: '',
            name: '',
            instrument_token: '',
            expiry: '',
            multiplier: '1'
        },
        script2: {
            side: 'SELL',
            scriptId: '',
            name: '',
            instrument_token: '',
            expiry: '',
            multiplier: '1'
        }
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Memoize instruments to prevent infinite re-renders
    const instruments = useMemo(() => {
        return instrumentData?.docs || [];
    }, [instrumentData?.docs]);

    // Fetch instruments on modal open
    useEffect(() => {
        if (open) {
            dispatch(getInstrumentList());
        }
    }, [dispatch, open]);

    // Handle script selection - memoized to prevent re-creation
    const handleScriptSelect = useCallback((scriptType, script) => {
        if (script) {
            setFormData(prev => ({
                ...prev,
                [scriptType]: {
                    ...prev[scriptType],
                    scriptId: script._id,
                    name: script.tradingsymbol,
                    instrument_token: script.instrument_token,
                    expiry: script.expiry
                }
            }));
        } else {
            // Clear script when null is selected
            setFormData(prev => ({
                ...prev,
                [scriptType]: {
                    ...prev[scriptType],
                    scriptId: '',
                    name: '',
                    instrument_token: '',
                    expiry: ''
                }
            }));
        }
    }, []);

    // Handle side change - memoized to prevent re-creation
    const handleSideChange = useCallback((scriptType, side) => {
        setFormData(prev => ({
            ...prev,
            [scriptType]: {
                ...prev[scriptType],
                side
            }
        }));
    }, []);

    // Handle multiplier change - memoized to prevent re-creation
    const handleMultiplierChange = useCallback((scriptType, multiplier) => {
        setFormData(prev => ({
            ...prev,
            [scriptType]: {
                ...prev[scriptType],
                multiplier
            }
        }));
    }, []);

    // Validate form - memoized to prevent re-creation
    const isFormValid = useCallback(() => {
        return formData.script1.scriptId &&
            formData.script2.scriptId &&
            formData.script1.multiplier &&
            formData.script2.multiplier &&
            formData.script1.side !== formData.script2.side;
    }, [formData.script1.scriptId, formData.script1.side, formData.script1.multiplier, formData.script2.scriptId, formData.script2.side, formData.script2.multiplier]);

    // Handle close - memoized to prevent re-creation
    const handleClose = useCallback(() => {
        // Reset form
        setFormData({
            script1: {
                side: 'BUY',
                scriptId: '',
                name: '',
                instrument_token: '',
                expiry: '',
                multiplier: '1'
            },
            script2: {
                side: 'SELL',
                scriptId: '',
                name: '',
                instrument_token: '',
                expiry: '',
                multiplier: '1'
            }
        });
        onClose();
    }, [onClose]);

    // Handle save - memoized to prevent re-creation
    const handleSave = useCallback(async () => {
        if (!isFormValid()) {
            enqueueSnackbar('Please select both scripts with opposite sides', { variant: 'error' });
            return;
        }

        setIsSubmitting(true);

        try {
            // Generate unique ID for the pair
            const pairId = Date.now().toString();

            // Create hedging pair object
            const hedgingPair = {
                id: pairId,
                script1: formData.script1,
                script2: formData.script2,
                createdAt: new Date().toISOString()
            };

            // Get existing watchlist from localStorage
            const existingWatchlist = JSON.parse(localStorage.getItem('hedgingWatchlist') || '[]');

            // Add new pair
            const updatedWatchlist = [...existingWatchlist, hedgingPair];

            // Save to localStorage
            localStorage.setItem('hedgingWatchlist', JSON.stringify(updatedWatchlist));

            enqueueSnackbar('Hedging pair added successfully', { variant: 'success' });
            handleClose();
        } catch (error) {
            console.error('Error saving hedging pair:', error);
            enqueueSnackbar('Failed to add hedging pair', { variant: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    }, [formData, isFormValid, enqueueSnackbar, handleClose]);

    // Get side chip - memoized to prevent re-creation
    const getSideChip = useCallback((side) => (
        <Chip
            label={side}
            size="small"
            variant="filled"
            sx={{
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 600,
                backgroundColor: side === 'BUY'
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                color: 'white',
                borderRadius: '4px',
                minWidth: 45,
            }}
        />
    ), [theme.palette.success.main, theme.palette.error.main]);

    // Render script card - memoized to prevent re-creation
    const renderScriptCard = useCallback((scriptType, title) => {
        const script = formData[scriptType];
        const isScript1 = scriptType === 'script1';

        return (
            <CompactCard side={script.side}>
                <CompactCardHeader side={script.side}>
                    <Iconify
                        icon={isScript1 ? "eva:trending-up-fill" : "eva:trending-down-fill"}
                        sx={{ fontSize: 20, color: script.side === 'BUY' ? 'success.dark' : 'error.dark' }}
                    />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                        {title}
                    </Typography>
                    {getSideChip(script.side)}
                </CompactCardHeader>

                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                    <Stack spacing={2}>
                        {/* Side Selection */}
                        <FormControl size="small" fullWidth>
                            <InputLabel>Side</InputLabel>
                            <Select
                                value={script.side}
                                label="Side"
                                onChange={(e) => handleSideChange(scriptType, e.target.value)}
                                sx={{ fontSize: '0.85rem' }}
                            >
                                <MenuItem value="BUY">BUY</MenuItem>
                                <MenuItem value="SELL">SELL</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Script Search */}
                        <Box sx={{ position: 'relative' }} className="script-search-container">
                            <Autocomplete
                                fullWidth
                                size="small"
                                options={instruments}
                                getOptionLabel={(option) => option.tradingsymbol || ''}
                                value={instruments.find(inst => inst._id === script.scriptId) || null}
                                onChange={(event, newValue) => {
                                    handleScriptSelect(scriptType, newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        label="Search Script"
                                        placeholder="Type to search instruments..."
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                minHeight: '32px',
                                                fontSize: '0.85rem',
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.85rem',
                                            },
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <Iconify
                                                    icon="eva:search-fill"
                                                    sx={{ color: 'text.disabled', mr: 1, fontSize: 16 }}
                                                />
                                            ),
                                            endAdornment: (
                                                <>
                                                    {script.scriptId && (
                                                        <Iconify
                                                            icon="eva:checkmark-circle-fill"
                                                            sx={{ color: 'success.main', ml: 1, fontSize: 16 }}
                                                        />
                                                    )}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box {...props} key={option._id}>
                                        <Box>
                                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                                                {modifyScriptName(option.tradingsymbol, option.expiry)}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                                {option.exchange?.name} • {option.segment}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                ListboxProps={{
                                    style: {
                                        maxHeight: 200,
                                    },
                                }}
                                filterOptions={(options, { inputValue }) => {
                                    if (!inputValue || inputValue.length < 1) {
                                        return [];
                                    }

                                    return options.filter(option =>
                                        option.tradingsymbol.toLowerCase().includes(inputValue.toLowerCase()) ||
                                        option.name?.toLowerCase().includes(inputValue.toLowerCase())
                                    ).slice(0, 20);
                                }}
                            />
                        </Box>

                        {/* Multiplier Field */}
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Multiplier"
                            placeholder="1.0"
                            value={script.multiplier}
                            onChange={(e) => handleMultiplierChange(scriptType, e.target.value)}
                            sx={{
                                '& .MuiInputBase-root': {
                                    minHeight: '32px',
                                    fontSize: '0.85rem',
                                },
                                '& .MuiInputLabel-root': {
                                    fontSize: '0.85rem',
                                },
                            }}
                            InputProps={{
                                inputProps: { min: 0, step: 0.01 }
                            }}
                        />
                    </Stack>
                </CardContent>
            </CompactCard>
        );
    }, [formData, instruments, handleSideChange, handleScriptSelect, handleMultiplierChange, getSideChip, modifyScriptName]);

    // Validation message - memoized to prevent re-creation
    const getValidationMessage = useCallback(() => {
        if (!formData.script1.scriptId || !formData.script2.scriptId) {
            return 'Please select both scripts';
        }
        if (!formData.script1.multiplier || !formData.script2.multiplier) {
            return 'Please enter multiplier values for both scripts';
        }
        if (formData.script1.side === formData.script2.side) {
            return 'Scripts must have opposite sides (one BUY, one SELL)';
        }
        return '';
    }, [formData.script1.scriptId, formData.script1.side, formData.script1.multiplier, formData.script2.scriptId, formData.script2.side, formData.script2.multiplier]);

    return (
        <StyledDialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Iconify icon="mdi:chart-multiple" sx={{ fontSize: 24, color: 'primary.main' }} />
                    <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                        Add Hedging Pair
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        {renderScriptCard('script1', 'Script 1')}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {renderScriptCard('script2', 'Script 2')}
                    </Grid>
                </Grid>

                {/* Validation Message */}
                {getValidationMessage() && (
                    <Box sx={{ mt: 2 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'error.main',
                                fontSize: '0.75rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                            }}
                        >
                            <Iconify icon="eva:alert-circle-fill" sx={{ fontSize: 16 }} />
                            {getValidationMessage()}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0 }}>
                <Button onClick={handleClose} color="inherit">
                    Cancel
                </Button>
                <LoadingButton
                    onClick={handleSave}
                    loading={isSubmitting}
                    disabled={!isFormValid()}
                    variant="contained"
                    startIcon={<Iconify icon="eva:plus-fill" />}
                    sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                        }
                    }}
                >
                    Add Hedging Pair
                </LoadingButton>
            </DialogActions>
        </StyledDialog>
    );
};

export default HedgingWatchlistModal;
